import { request } from './client';

/**
 * EVERY ROW OF A PAGED LIST — for the small masters a screen needs whole (staff, products, territories).
 *
 * Screens used to ask for `pageSize: 200` (or 500, or 1000) and treat the answer as the whole list. The server clamps a
 * page at 1000, and nothing said when a list ran past the number asked for: the 201st sales person was simply not in the
 * picker, the 1001st dealer not in the ageing report. This walks the pages until `meta.total` is reached.
 *
 * Returns the same `{ data, meta }` shape `request` gives for a paged endpoint, so it drops in where that call was.
 * Pickers over the big books (retailers, farmers) search the server instead — see `hooks/useRecordSearch`.
 */
export async function requestAllPages<T = any>(
  url: string,
  params: Record<string, unknown> = {},
  pageSize = 500,
): Promise<{ data: T[]; meta: { page: number; pageSize: number; total: number } }> {
  const first = await request<any>({ url, params: { ...params, page: 1, pageSize } });
  // A list endpoint that is not paged hands back the bare array: that already is the whole list.
  if (Array.isArray(first)) return { data: first, meta: { page: 1, pageSize: first.length, total: first.length } };
  const rows: T[] = [...(first?.data ?? [])];
  const total = Number(first?.meta?.total ?? rows.length);
  // The server's page size wins (it clamps what it is asked for), so step by what it actually returned.
  const step = Number(first?.meta?.pageSize ?? pageSize) || pageSize;
  const pages = Math.ceil(total / step);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, i) => request<any>({ url, params: { ...params, page: i + 2, pageSize: step } })),
  );
  for (const r of rest) rows.push(...(Array.isArray(r) ? r : (r?.data ?? [])));
  return { data: rows, meta: { page: 1, pageSize: rows.length, total } };
}
